import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Mic, Clock, Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, RefreshCw, ChevronDown } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

// Define language settings object, mapping scene to corresponding language code
const languageSettings = {
  // English exam types (keep original English exams unchanged)
  'IELTS': { code: 'en-US', name: 'English' },
  'TOEFL': { code: 'en-US', name: 'English' },
  'CET4': { code: 'en-US', name: 'English' },
  'CET6': { code: 'en-US', name: 'English' },
  'CPE': { code: 'en-US', name: 'English' },
  'CAE': { code: 'en-US', name: 'English' },
  
  // French exam types (4 authoritative exams)
  'DELF': { code: 'fr-FR', name: 'French' },
  'DALF': { code: 'fr-FR', name: 'French' },
  'TCF': { code: 'fr-FR', name: 'French' },
  'TEF': { code: 'fr-FR', name: 'French' },
  
  // Spanish exam types (4 authoritative exams)
  'DELE': { code: 'es-ES', name: 'Spanish' },
  'SIELE': { code: 'es-ES', name: 'Spanish' },
  'CELU': { code: 'es-ES', name: 'Spanish' },
  'DIE': { code: 'es-ES', name: 'Spanish' },
  
  // Japanese exam types (4 authoritative exams)
  'JLPT': { code: 'ja-JP', name: 'Japanese' },
  'JPT': { code: 'ja-JP', name: 'Japanese' },
  'J.TEST': { code: 'ja-JP', name: 'Japanese' },
  'NAT-TEST': { code: 'ja-JP', name: 'Japanese' },
  
  // Korean exam types (4 authoritative exams)
  'TOPIK': { code: 'ko-KR', name: 'Korean' },
  'KLPT': { code: 'ko-KR', name: 'Korean' },
  'FLEX': { code: 'ko-KR', name: 'Korean' },
  'EPS-TOPIK': { code: 'ko-KR', name: 'Korean' },
  
  // Portuguese exam types (4 authoritative exams)
  'CAPLE': { code: 'pt-PT', name: 'Portuguese' },
  'CELPE-Bras': { code: 'pt-PT', name: 'Portuguese' },
  'PLE': { code: 'pt-PT', name: 'Portuguese' },
  'DEPLE': { code: 'pt-PT', name: 'Portuguese' },
  
  // German exam types (4 authoritative exams)
  'TestDaF': { code: 'de-DE', name: 'German' },
  'Goethe': { code: 'de-DE', name: 'German' },
  'DSH': { code: 'de-DE', name: 'German' },
  'telc': { code: 'de-DE', name: 'German' },
  
  // Italian exam types (4 authoritative exams)
  'CELI': { code: 'it-IT', name: 'Italian' },
  'CILS': { code: 'it-IT', name: 'Italian' },
  'PLIDA': { code: 'it-IT', name: 'Italian' },
  'IT': { code: 'it-IT', name: 'Italian' },
  
  // Russian exam types (4 authoritative exams)
  'TORFL': { code: 'ru-RU', name: 'Russian' },
  'TRKI': { code: 'ru-RU', name: 'Russian' },
  'TELC Russian': { code: 'ru-RU', name: 'Russian' },
  'RCT': { code: 'ru-RU', name: 'Russian' },
  
  // Arabic exam types (4 authoritative exams)
  'ALPT': { code: 'ar-SA', name: 'Arabic' },
  'AAAT': { code: 'ar-SA', name: 'Arabic' },
  'ACTFL-OPI': { code: 'ar-SA', name: 'Arabic' },
  'IB Arabic': { code: 'ar-SA', name: 'Arabic' },
  
  // Dutch exam types (4 authoritative exams)
  'CNaVT': { code: 'nl-NL', name: 'Dutch' },
  'NT2': { code: 'nl-NL', name: 'Dutch' },
  'CNAVT': { code: 'nl-NL', name: 'Dutch' },
  'Staatsexamen': { code: 'nl-NL', name: 'Dutch' }
};

const TalktasticVoiceGame = ({ initialScene = 'IELTS', showRules, setShowRules }) => {
  const [position, setPosition] = useState(0);
  const [diceValue, setDiceValue] = useState(1);
  const [prepTime, setPrepTime] = useState(60); // Preparation time
  const [practiceTime, setPracticeTime] = useState(180); // Practice time
  const [timer, setTimer] = useState(prepTime);
  const [gamePhase, setGamePhase] = useState('idle');
  const [bombTask, setBombTask] = useState(null);
  const [currentScene, setCurrentScene] = useState(initialScene || 'IELTS');
  const [showSceneSelector, setShowSceneSelector] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 60, left: 0, width: 0 });
  
  // Add speech recognition related states
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [showTranscriptionOptions, setShowTranscriptionOptions] = useState(false);
  
  // Speech recognition reference
  const recognitionRef = useRef(null);
  // Save accumulated recognition results
  const fullTranscriptionRef = useRef('');
  
  // Get current scene language settings using useMemo
  const currentLanguageSetting = useMemo(() => {
    return languageSettings[currentScene] || languageSettings['IELTS']; // Default to English
  }, [currentScene]);
  
  // Using useMemo to wrap examTopics object, avoid recreating it on each render
  const examTopics = useMemo(() => ({
    // English exam topics
    'IELTS': [
      "Family Values", "Online Shopping", "Social Media Influence", "Work-Life Balance", 
      "Public Transportation", "Healthy Lifestyle", "Stress Management", "Community Service", 
      "Digital Addiction", "Fast Food", "Recycling", "Renewable Energy", "Plastic Pollution", 
      "Volunteer Work", "Gender Roles", "Peer Pressure", "Bullying", "Mental Health Awareness", 
      "Parenting Styles", "Elderly Care", "Youth Employment", "Cultural Diversity", "Affordable Housing", 
      "Online Dating"
    ],
    'TOEFL': [
      "Academic Integrity", "Campus Life", "Research Methods", "Critical Thinking", 
      "Study Abroad Programs", "Online Learning", "Scientific Discovery", "Historical Analysis", 
      "Literary Interpretation", "Group Projects", "Class Participation", "Student Organizations", 
      "Academic Writing", "Thesis Development", "College Athletics", "Cultural Exchange Programs", 
      "Student Housing", "Library Resources", "Time Management", "Laboratory Experiments", 
      "Academic Presentations", "Scholarship Applications", "Career Planning", "University Admissions"
    ],
    'CET4': [
      "Friendship", "Part-time Jobs", "Hobbies", "Travel Experience", 
      "Favorite Books", "Movie Reviews", "College Life", "Music Appreciation", 
      "Sports Activities", "Technology Use", "Campus Clubs", "Holiday Celebrations", 
      "Outdoor Adventures", "Learning Methods", "Dormitory Life", "Social Media", 
      "Environmental Protection", "Health Habits", "Family Relationships", "Shopping Habits", 
      "Cultural Differences", "Future Plans", "Hometown Description", "Childhood Memories"
    ],
    'CET6': [
      "Global Economy", "Scientific Research", "Cultural Identity", "International Relations", 
      "Career Development", "Educational Reform", "Technological Innovation", "Social Movements", 
      "Environmental Policies", "Media Influence", "Political Systems", "Population Issues", 
      "Urban Planning", "Natural Disasters", "Climate Change", "Healthcare Systems", 
      "Immigration Policies", "Digital Revolution", "Economic Inequality", "Legal Systems", 
      "International Cooperation", "Human Rights", "Gender Equality", "Globalization Effects"
    ],
    'CPE': [
      "Literary Analysis", "Linguistic Studies", "Cultural Comparison", "Translation Techniques", 
      "Poetry Interpretation", "Academic Writing", "Speech Delivery", "Communication Theories", 
      "English Literature", "Western Culture", "Language Acquisition", "Rhetorical Devices", 
      "Cross-cultural Communication", "Terminology Analysis", "Text Comprehension", "Grammar Structures", 
      "Discourse Analysis", "American Literature", "British Culture", "Language History", 
      "Writing Techniques", "Professional Communication", "Public Speaking", "Media Language"
    ],
    'CAE': [
      "Literary Criticism", "Comparative Literature", "Advanced Linguistics", "Syntax Analysis", 
      "Stylistic Features", "Theoretical Frameworks", "Cultural Anthropology", "Sociolinguistics", 
      "Historical Linguistics", "Semantic Analysis", "Phonological Systems", "Language Philosophy", 
      "Western Civilization", "Literary Movements", "Critical Theory", "Research Methodology", 
      "Discourse Ethics", "Academic Publishing", "Language Policy", "Translation Studies", 
      "Corpus Linguistics", "Pragmatics", "English Etymology", "Cognitive Linguistics"
    ],
    
    // French exam topics (in French)
    'DELF': [
      "La famille", "Les voyages", "La technologie", "L'environnement",
      "L'éducation", "La cuisine", "Les loisirs", "Les médias",
      "La santé", "Le travail", "La culture", "Le sport",
      "La mode", "Les transports", "Le logement", "L'amitié",
      "Les achats", "Les fêtes", "La musique", "Le cinéma",
      "Les animaux", "La littérature", "La politique", "L'histoire"
    ],
    'DALF': [
      "Les enjeux sociaux", "La mondialisation", "Les débats contemporains", "L'art contemporain",
      "Les sciences", "L'économie mondiale", "La philosophie", "La psychologie",
      "Le développement durable", "Les droits humains", "L'immigration", "Les médias sociaux",
      "La diversité culturelle", "L'éthique professionnelle", "La recherche scientifique", "Les innovations technologiques",
      "Le système éducatif", "La santé publique", "La justice sociale", "Les relations internationales",
      "Le changement climatique", "L'urbanisme", "La démographie", "Les mouvements sociaux"
    ],
    'TCF': [
      "La vie quotidienne", "Les traditions", "Les relations personnelles", "Les études",
      "Le monde du travail", "Les nouvelles technologies", "Les médias", "Les activités culturelles",
      "Les voyages", "La gastronomie", "La santé", "Le logement",
      "La nature", "La ville", "La campagne", "Les transports",
      "Les achats", "Les services", "Les loisirs", "La météo",
      "Les fêtes", "Les sports", "La communication", "Les projets personnels"
    ],
    'TEF': [
      "La vie sociale", "La consommation", "L'actualité", "La communication",
      "Les défis contemporains", "L'expression artistique", "Le patrimoine culturel", "L'innovation",
      "L'échange interculturel", "Les systèmes politiques", "L'éducation supérieure", "L'évolution sociale",
      "Les tendances économiques", "La diversité linguistique", "La responsabilité sociale", "L'identité nationale",
      "Le progrès technologique", "Les questions éthiques", "La migration internationale", "La protection de l'environnement",
      "La qualité de vie", "Les perspectives professionnelles", "La coopération internationale", "Le développement personnel"
    ],
    
    // Spanish exam topics (in Spanish)
    'DELE': [
      "La familia", "El trabajo", "Los estudios", "Los viajes",
      "El tiempo libre", "La comida", "La salud", "La vivienda",
      "Las compras", "El clima", "La tecnología", "Los medios de comunicación",
      "Las redes sociales", "El medio ambiente", "La cultura", "El deporte",
      "Las celebraciones", "Las tradiciones", "El transporte", "La moda",
      "La música", "El cine", "La literatura", "El arte"
    ],
    'SIELE': [
      "La vida cotidiana", "Las relaciones personales", "La educación", "El mundo laboral",
      "El ocio", "El turismo", "La gastronomía", "Los servicios públicos",
      "El bienestar", "La comunicación", "La ciencia", "La sociedad",
      "La economía", "La política", "La historia", "La geografía",
      "La cultura hispana", "Los medios de comunicación", "La tecnología", "El mundo digital",
      "La naturaleza", "Los fenómenos sociales", "Los movimientos culturales", "Los desafíos globales"
    ],
    'DIE': [
      "La comunicación", "El aprendizaje", "La competencia lingüística", "La comprensión oral",
      "La expresión escrita", "La gramática", "El vocabulario", "Las habilidades comunicativas",
      "La fonética", "La pragmática", "La sociolingüística", "La morfología",
      "El análisis textual", "La argumentación", "La narración", "La descripción",
      "La exposición", "El diálogo", "La correspondencia", "Los géneros textuales",
      "La interpretación", "La traducción", "La variación lingüística", "La competencia cultural"
    ],
    'CELU': [
      "La vida universitaria", "El mundo profesional", "La comunicación intercultural", "La identidad cultural",
      "Las manifestaciones artísticas", "Los medios de difusión", "La innovación tecnológica", "La investigación científica",
      "El desarrollo social", "La diversidad lingüística", "Los sistemas educativos", "La responsabilidad civil",
      "El patrimonio cultural", "Las expresiones populares", "Los fenómenos urbanos", "Las transformaciones sociales",
      "Los movimientos migratorios", "La integración regional", "Las políticas públicas", "La cooperación internacional",
      "La preservación ambiental", "La construcción ciudadana", "La identidad nacional", "La globalización"
    ],
    
    // Japanese exam topics (in romaji/english)
    'JLPT': [
      "Jiko Shoukai", "Kazoku", "Shumi", "Ryokou",
      "Ryouri", "Kenkō", "Tenki", "Machi",
      "Uchi", "Gakkou", "Shigoto", "Yume",
      "Tomodachi", "Denshi Kiki", "Souji", "Kaimono",
      "Ongaku", "Eiga", "Hon", "Supōtsu",
      "Matsuri", "Nichijou Seikatsu", "Bunka", "Rekishi"
    ],
    'JPT': [
      "Aisatsu", "Yakusoku", "Kaiwa", "Shoukai",
      "Kibun", "Seikaku", "Taido", "Fuan",
      "Yorokobi", "Ikari", "Kanashimi", "Kyoumi",
      "Mondai", "Kibou", "Mokuteki", "Doryoku",
      "Manzoku", "Mushi", "Omoide", "Kansou",
      "Kaiketsu", "Aimai", "Sentaku", "Kettei"
    ],
    'J.TEST': [
      "Bijinesu Manā", "Kaigi", "Teian", "Houkoku",
      "Touron", "Settoku", "Koshou", "Keiyaku",
      "Torihiki", "Kyaku Taiou", "Kaihatsu", "Kigyō Bunka",
      "Keiei Senryaku", "Rīdāshippu", "Chīmu Wāku", "Purojakuto Kanri",
      "Senden", "Māketingu", "Shijō Chōsa", "Kyōsō Bunseki",
      "Shōhin Kaihatsu", "Hinshitsu Kanri", "Gurōbaru Bijinesu", "Kokusai Kankei"
    ],
    'NAT-TEST': [
      "Ningen Kankei", "Seikatsu Shūkan", "Gakushū Houhou", "Komyunikēshon",
      "Bunka Hikaku", "Media Riyou", "Kankyō Mondai", "Shakai Mondai",
      "Keizai Katsudou", "Kagaku Gijutsu", "Kyōiku Mondai", "Kenkō Kanri",
      "Shokuji Bunka", "Fasshon", "Entāteinmento", "Dentō Bunka",
      "Gendai Shakai", "Toshi Mondai", "Nōson Mondai", "Jinkō Mondai",
      "Kaigai Ryūgaku", "Kokusai Kōryū", "Nihon Bunka", "Sekai Bunka"
    ],

    // Additional languages and their exam topics would follow the same pattern...
    
    // Korean exam topics
    'TOPIK': [
      "Gajok", "Ilsang Saenghwal", "Chwimi", "Yeoghaeng",
      "Eumshik", "Geongang", "Nalshhi", "Doshi",
      "Jib", "Hakgyo", "Jig-eob", "Kkum",
      "Chingu", "Gigi", "Cheongso", "Shoping",
      "Eumak", "Yeonghwa", "Chaek", "Undong",
      "Chukje", "Munhwa", "Yeoksa", "Sahoe"
    ],
    
    // ... remaining language topics (add other language exam topics here)

    // Let's add placeholders for the other language exams
    'KLPT': getBoardTopics('Korean'),
    'FLEX': getBoardTopics('Korean'), 
    'EPS-TOPIK': getBoardTopics('Korean'),
    
    'CAPLE': getBoardTopics('Portuguese'),
    'CELPE-Bras': getBoardTopics('Portuguese'),
    'PLE': getBoardTopics('Portuguese'),
    'DEPLE': getBoardTopics('Portuguese'),
    
    'TestDaF': getBoardTopics('German'),
    'Goethe': getBoardTopics('German'),
    'DSH': getBoardTopics('German'),
    'telc': getBoardTopics('German'),
    
    'CELI': getBoardTopics('Italian'),
    'CILS': getBoardTopics('Italian'),
    'PLIDA': getBoardTopics('Italian'),
    'IT': getBoardTopics('Italian'),
    
    'TORFL': getBoardTopics('Russian'),
    'TRKI': getBoardTopics('Russian'),
    'TELC Russian': getBoardTopics('Russian'),
    'RCT': getBoardTopics('Russian'),
    
    'ALPT': getBoardTopics('Arabic'),
    'AAAT': getBoardTopics('Arabic'),
    'ACTFL-OPI': getBoardTopics('Arabic'),
    'IB Arabic': getBoardTopics('Arabic'),
    
    'CNaVT': getBoardTopics('Dutch'),
    'NT2': getBoardTopics('Dutch'),
    'CNAVT': getBoardTopics('Dutch'),
    'Staatsexamen': getBoardTopics('Dutch')
  }), []);
  
  // Helper function to get a set of board topics for a language
  function getBoardTopics(language) {
    // Common topics across all languages
    const commonTopics = [
      // Set 1 - Basic topics
      [
        "Family", "Travel", "Food", "Technology", 
        "Education", "Entertainment", "Sports", "Work", 
        "Home", "Shopping", "Weather", "Transportation", 
        "Health", "Hobbies", "Friends", "Culture", 
        "Nature", "City Life", "Holidays", "Communication", 
        "Music", "Films", "Books", "Animals"
      ],
      // Set 2 - Daily activities
      [
        "Morning Routine", "Cooking", "Exercise", "Social Media", 
        "Weekend Plans", "Restaurants", "Cafes", "Public Transport", 
        "Office Work", "School Life", "Cleaning", "Shopping Mall", 
        "Grocery Shopping", "Museums", "Concerts", "Sports Events", 
        "Birthday Celebrations", "Family Gatherings", "Vacation Planning", "Mobile Phones", 
        "Internet Usage", "Television Shows", "Fashion", "Pets"
      ],
      // Set 3 - Interests
      [
        "Photography", "Gardening", "Painting", "Dancing", 
        "Swimming", "Hiking", "Cycling", "Yoga", 
        "Chess", "Video Games", "Board Games", "Reading", 
        "Writing", "Singing", "Playing Instruments", "Collecting", 
        "Volunteering", "Learning Languages", "Baking", "Fitness", 
        "Meditation", "Traveling Abroad", "Camping", "DIY Projects"
      ],
      // Set 4 - Social topics
      [
        "Friendship", "Dating", "Marriage", "Childhood", 
        "Adolescence", "Adult Life", "Retirement", "Family Relations", 
        "Neighbors", "Community", "Social Events", "Cultural Traditions", 
        "Festivals", "Parties", "Social Norms", "Etiquette", 
        "Communication Skills", "Social Media Impact", "Online Communities", "Group Activities", 
        "Team Sports", "Clubs", "Social Organizations", "Networking"
      ],
      // Set 5 - Global issues
      [
        "Environment", "Climate Change", "Sustainability", "Recycling", 
        "Renewable Energy", "Global Health", "Education Systems", "Economic Development", 
        "Technology Impact", "Cultural Diversity", "Migration", "Urbanization", 
        "Rural Life", "Transportation Systems", "Digital Divide", "Media Influence", 
        "Global Collaboration", "International Relations", "Human Rights", "Gender Equality", 
        "Poverty Reduction", "Welfare Systems", "Conservation", "Innovation"
      ],
      // Set 6 - Personal development
      [
        "Goals", "Skills", "Challenges", "Achievements", 
        "Learning Process", "Career Development", "Self-improvement", "Education", 
        "Time Management", "Stress Management", "Financial Planning", "Health Habits", 
        "Work-Life Balance", "Decision Making", "Problem Solving", "Communication Skills", 
        "Leadership", "Teamwork", "Creativity", "Innovation", 
        "Adaptability", "Resilience", "Emotional Intelligence", "Critical Thinking"
      ]
    ];
    
    // Current word set index (0-5)
    // We'll store this in a module-level variable to persist between renders
    if (typeof window.currentWordSetIndex === 'undefined') {
      window.currentWordSetIndex = 0;
    }
    
    const getTranslatedTopics = () => {
      const currentTopics = commonTopics[window.currentWordSetIndex];
      
      // Translate based on language
      switch(language) {
        case 'English':
          return shuffleArray([...currentTopics]);
        case 'French':
          return shuffleArray([
            "Famille", "Voyage", "Nourriture", "Technologie",
            "Éducation", "Divertissement", "Sports", "Travail",
            "Maison", "Shopping", "Météo", "Transport",
            "Santé", "Loisirs", "Amis", "Culture",
            "Nature", "Vie urbaine", "Vacances", "Communication",
            "Musique", "Films", "Livres", "Animaux"
          ]);
        case 'Spanish':
          return shuffleArray([
            "Familia", "Viajes", "Comida", "Tecnología",
            "Educación", "Entretenimiento", "Deportes", "Trabajo",
            "Hogar", "Compras", "Clima", "Transporte",
            "Salud", "Pasatiempos", "Amigos", "Cultura",
            "Naturaleza", "Vida urbana", "Vacaciones", "Comunicación",
            "Música", "Películas", "Libros", "Animales"
          ]);
        case 'Japanese':
          return shuffleArray([
            "家族", "旅行", "食べ物", "テクノロジー",
            "教育", "娯楽", "スポーツ", "仕事",
            "家", "買い物", "天気", "交通",
            "健康", "趣味", "友達", "文化",
            "自然", "都市生活", "休日", "コミュニケーション",
            "音楽", "映画", "本", "動物"
          ]);
        case 'Korean':
          return shuffleArray([
            "가족", "여행", "음식", "기술",
            "교육", "엔터테인먼트", "스포츠", "일",
            "집", "쇼핑", "날씨", "교통",
            "건강", "취미", "친구", "문화",
            "자연", "도시 생활", "휴일", "의사소통",
            "음악", "영화", "책", "동물"
          ]);
        case 'Portuguese':
          return shuffleArray([
            "Família", "Viagem", "Comida", "Tecnologia",
            "Educação", "Entretenimento", "Esportes", "Trabalho",
            "Casa", "Compras", "Clima", "Transporte",
            "Saúde", "Passatempos", "Amigos", "Cultura",
            "Natureza", "Vida na cidade", "Férias", "Comunicação",
            "Música", "Filmes", "Livros", "Animais"
          ]);
        case 'German':
          return shuffleArray([
            "Familie", "Reisen", "Essen", "Technologie",
            "Bildung", "Unterhaltung", "Sport", "Arbeit",
            "Zuhause", "Einkaufen", "Wetter", "Verkehr",
            "Gesundheit", "Hobbys", "Freunde", "Kultur",
            "Natur", "Stadtleben", "Urlaub", "Kommunikation",
            "Musik", "Filme", "Bücher", "Tiere"
          ]);
        case 'Italian':
          return shuffleArray([
            "Famiglia", "Viaggi", "Cibo", "Tecnologia",
            "Istruzione", "Intrattenimento", "Sport", "Lavoro",
            "Casa", "Shopping", "Meteo", "Trasporto",
            "Salute", "Hobby", "Amici", "Cultura",
            "Natura", "Vita cittadina", "Vacanze", "Comunicazione",
            "Musica", "Film", "Libri", "Animali"
          ]);
        case 'Russian':
          return shuffleArray([
            "Семья", "Путешествия", "Еда", "Технологии",
            "Образование", "Развлечения", "Спорт", "Работа",
            "Дом", "Покупки", "Погода", "Транспорт",
            "Здоровье", "Хобби", "Друзья", "Культура",
            "Природа", "Городская жизнь", "Праздники", "Общение",
            "Музыка", "Фильмы", "Книги", "Животные"
          ]);
        case 'Arabic':
          return shuffleArray([
            "عائلة", "سفر", "طعام", "تكنولوجيا",
            "تعليم", "ترفيه", "رياضة", "عمل",
            "منزل", "تسوق", "طقس", "نقل",
            "صحة", "هوايات", "أصدقاء", "ثقافة",
            "طبيعة", "حياة المدينة", "عطلات", "تواصل",
            "موسيقى", "أفلام", "كتب", "حيوانات"
          ]);
        case 'Dutch':
          return shuffleArray([
            "Familie", "Reizen", "Eten", "Technologie",
            "Onderwijs", "Entertainment", "Sport", "Werk",
            "Huis", "Winkelen", "Weer", "Vervoer",
            "Gezondheid", "Hobby's", "Vrienden", "Cultuur",
            "Natuur", "Stadsleven", "Vakanties", "Communicatie",
            "Muziek", "Films", "Boeken", "Dieren"
          ]);
        default:
          return shuffleArray([...currentTopics]);
      }
    };
    
    // Helper function to shuffle array
    function shuffleArray(array) {
      const newArray = [...array]; // Create a copy to avoid mutating original
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]]; // Swap elements
      }
      return newArray;
    }
    
    return getTranslatedTopics();
  }
  
  // Set default topics to IELTS
  const [currentTopics, setCurrentTopics] = useState(examTopics[currentScene] || examTopics['IELTS']);
  
  // Initialize speech recognition
  useEffect(() => {
    // Check if browser supports speech recognition API
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.error('Your browser does not support speech recognition');
      return;
    }
    
    // Create speech recognition instance
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    
    // Configure speech recognition
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    
    // Set speech recognition language
    recognitionRef.current.lang = currentLanguageSetting.code;
    
    // Handle recognition results
    recognitionRef.current.onresult = (event) => {
      const results = event.results;
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < results.length; i++) {
        const transcript = results[i][0].transcript;
        if (results[i].isFinal) {
          fullTranscriptionRef.current += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Update currently displayed content
      setTranscription(fullTranscriptionRef.current + interimTranscript);
    };
    
    // Handle speech recognition end
    recognitionRef.current.onend = () => {
      if (isRecording && gamePhase === 'practice') {
        // If still in practice phase and should be recording, restart
        try {
          recognitionRef.current.start();
        } catch (e) {
          console.error('Failed to restart speech recognition:', e);
          setIsRecording(false);
        }
      } else {
        setIsRecording(false);
      }
    };
    
    // Handle speech recognition errors
    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        alert('Please allow microphone access to enable speech recognition');
      }
      setIsRecording(false);
    };
    
    return () => {
      // Clean up speech recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors
        }
      }
    };
  }, [isRecording, gamePhase, currentLanguageSetting]);
  
  // Listen for scene changes, update language settings
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = currentLanguageSetting.code;
      console.log(`Speech recognition language switched to: ${currentLanguageSetting.name} (${currentLanguageSetting.code})`);
    }
  }, [currentScene, currentLanguageSetting]);
  
  // Update to specific exam type topics
  const changeExamType = (examType) => {
    setCurrentScene(examType);
    setCurrentTopics(examTopics[examType]);
    setPosition(0);
    setGamePhase('idle');
    setTimer(prepTime);
    setBombTask(null);
    setShowSceneSelector(false);
    setIsRecording(false);
    setTranscription('');
    setShowTranscriptionOptions(false);
    
    // If recording, stop first
    if (isRecording && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors
      }
      setIsRecording(false);
    }
    
    // Send custom event to notify App component that scene has changed
    const event = new CustomEvent('sceneChange', { detail: { scene: examType } });
    window.dispatchEvent(event);
  };
  
  // Start/stop speech recognition
  const toggleRecording = () => {
    if (!recognitionRef.current) return;
    
    if (!isRecording) {
      // When starting new recording, reset accumulated results
      fullTranscriptionRef.current = '';
      
      // Ensure using correct language
      const languageSetting = currentLanguageSetting;
      recognitionRef.current.lang = languageSetting.code;
      
      try {
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (e) {
        console.error('Unable to start speech recognition:', e);
        alert('Failed to start speech recognition, please refresh the page and try again');
      }
    } else {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors
      }
      setIsRecording(false);
    }
  };
  
  // Start game
  const rollDice = () => {
    if (gamePhase !== 'idle') return;
    
    // Random dice value 1-6
    const newDiceValue = Math.floor(Math.random() * 6) + 1;
    setDiceValue(newDiceValue);
    
    // Calculate new position
    const newPosition = Math.min(position + newDiceValue, currentTopics.length - 1);
      setPosition(newPosition);
    
    // Check if landed on a bomb
      if (bombPositions.includes(newPosition)) {
      const bombIndex = bombPositions.indexOf(newPosition);
      setBombTask(bombTasks[bombIndex]);
      } else {
        setBombTask(null);
      }
    
    // Start preparation phase
    setGamePhase('prep');
    setTimer(prepTime);
    
    // Reset speech recognition state
    fullTranscriptionRef.current = '';
    setIsRecording(false);
    setTranscription('');
    setShowTranscriptionOptions(false);
    
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };
  
  // Listen for scene selector trigger event
  useEffect(() => {
    const handleSceneSelectorTrigger = (event) => {
      const { top, left, width } = event.detail;
      setDropdownPosition({ top, left, width });
    };
    
    window.addEventListener('sceneSelectorTrigger', handleSceneSelectorTrigger);
    return () => window.removeEventListener('sceneSelectorTrigger', handleSceneSelectorTrigger);
  }, []);
  
  // Add scene-selector-trigger class to a hidden element in document.body
  useEffect(() => {
    // Create a hidden trigger element
    const triggerExists = document.querySelector('.scene-selector-trigger');
    if (!triggerExists) {
      const trigger = document.createElement('button');
      trigger.className = 'scene-selector-trigger hidden';
      trigger.style.display = 'none';
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        setShowSceneSelector(!showSceneSelector);
      });
      document.body.appendChild(trigger);
      
      return () => {
        if (document.body.contains(trigger)) {
          document.body.removeChild(trigger);
        }
      };
    }
  }, [showSceneSelector]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const closeSelector = (e) => {
      // If click is not inside the selector, close it
      const dropdown = document.querySelector('.scene-dropdown');
      if (showSceneSelector && dropdown && !dropdown.contains(e.target) && 
          !e.target.classList.contains('scene-selector-trigger') && 
          !e.target.classList.contains('nav-button')) {
        setShowSceneSelector(false);
      }
    };
    
    if (showSceneSelector) {
      document.addEventListener('click', closeSelector);
    }
    return () => document.removeEventListener('click', closeSelector);
  }, [showSceneSelector]);
  
  const bombPositions = [4, 11, 19];
  const bombTasks = [
    "Create a sentence using the current word",
    "Say three words related to the current topic",
    "Start a short story with the current word"
  ];

  const diceIcons = [
    <Dice1 key="dice1" size={40} />,
    <Dice2 key="dice2" size={40} />,
    <Dice3 key="dice3" size={40} />,
    <Dice4 key="dice4" size={40} />,
    <Dice5 key="dice5" size={40} />,
    <Dice6 key="dice6" size={40} />
  ];

  const gameRules = `Game Rules:
1. Click the "Roll Dice" button to start the game.
2. Move to the corresponding topic square based on the dice roll.
3. The system will give you time to prepare for the topic.
4. When preparation time ends, recording will automatically begin, and you'll need to discuss the topic in your selected language.
5. After finishing, you can review your speaking content.
6. If you land on a square with a bomb symbol, you'll need to complete an additional challenge.

Tips:
- Try to use complete sentences and rich vocabulary to express your views.
- Consider exploring topics from multiple angles (Who/When/Where/Why).
- Speech recognition uses the selected language, so try to speak clearly.`;
  
  // Game timer
  useEffect(() => {
    if (gamePhase === 'idle' || gamePhase === 'completed') {
      return;
    }
    
    let countdown = null;
    
    // Set timer
    countdown = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          clearInterval(countdown);
          
          if (gamePhase === 'prep') {
            // Preparation phase ended, start practice phase
            setGamePhase('practice');
            setTimer(practiceTime);
            
            // Start speech recognition
            if (recognitionRef.current) {
              fullTranscriptionRef.current = ''; // Reset previous recognition results
              const languageSetting = currentLanguageSetting;
              recognitionRef.current.lang = languageSetting.code;
              
              try {
                recognitionRef.current.start();
                setIsRecording(true);
              } catch (e) {
                console.error('Unable to start speech recognition:', e);
              }
            }
          } else if (gamePhase === 'practice') {
            // Practice phase ended, complete game
            setGamePhase('idle');
            
            // Ensure speech recognition is stopped
            if (recognitionRef.current) {
              try {
                recognitionRef.current.stop();
                setIsRecording(false);
              } catch (e) {
                console.error('Error stopping speech recognition:', e);
              }
            }
            
            // Show result options
            setShowTranscriptionOptions(true);
          }
          
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
    
    return () => {
      if (countdown) {
        clearInterval(countdown);
      }
    };
  }, [gamePhase, practiceTime]);
  
  // Update vocabulary
  const updateVocab = () => {
    // Get the current language name
    const currentLanguage = currentLanguageSetting.name;
    
    // Rotate to next word set
    window.currentWordSetIndex = (window.currentWordSetIndex + 1) % 6;
    
    // Get topics for the current language
    const newTopics = getBoardTopics(currentLanguage);
    
    // Update topics
    setCurrentTopics(newTopics);
    setPosition(0);
    setGamePhase('idle');
    setTimer(prepTime);
    setBombTask(null);
    setTranscription('');
    setShowTranscriptionOptions(false);
  };
  
  // Update topics when scene/language changes
  useEffect(() => {
    const langSetting = currentLanguageSetting;
    const newTopics = getBoardTopics(langSetting.name);
    setCurrentTopics(newTopics);
  }, [currentScene, currentLanguageSetting]);
  
  const adjustPrepTime = (amount) => {
    setPrepTime(Math.max(10, prepTime + amount));
  };
  
  const adjustPracticeTime = (amount) => {
    setPracticeTime(Math.max(30, practiceTime + amount));
  };
  
  // View premium analysis
  const viewPaidAnalysis = () => {
    // Premium logic can be added here
    alert('Premium features are not yet implemented, this function will be available soon');
    setShowTranscriptionOptions(false);
  };

  // 手绘风格的按钮SVG
  const HandDrawnButton = ({ type, size = 20 }) => {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" className="mx-auto">
        <filter id="shaky-button">
          <feTurbulence type="turbulence" baseFrequency="0.05" numOctaves="2" result="turbulence"/>
          <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="1.5" xChannelSelector="R" yChannelSelector="G"/>
        </filter>
        <g style={{ filter: 'url(#shaky-button)' }}>
          {type === 'plus' ? (
            <>
              <circle cx="12" cy="12" r="10" fill="none" stroke="#1e62d0" strokeWidth="1.5" />
              <path d="M7,12 L17,12 M12,7 L12,17" fill="none" stroke="#1e62d0" strokeWidth="2" strokeLinecap="round" />
            </>
          ) : (
            <>
              <circle cx="12" cy="12" r="10" fill="none" stroke="#1e62d0" strokeWidth="1.5" />
              <path d="M7,12 L17,12" fill="none" stroke="#1e62d0" strokeWidth="2" strokeLinecap="round" />
            </>
          )}
        </g>
      </svg>
    );
  };
  
  // 手绘闹钟SVG
  const HandDrawnClock = () => (
    <div className="relative">
      <svg width="120" height="120" viewBox="0 0 100 100" className="mx-auto">
        <filter id="shaky">
          <feTurbulence type="turbulence" baseFrequency="0.05" numOctaves="2" result="turbulence"/>
          <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="2" xChannelSelector="R" yChannelSelector="G"/>
        </filter>
        <g style={{ filter: 'url(#shaky)' }}>
          <circle cx="50" cy="50" r="40" fill="none" stroke="#1e62d0" strokeWidth="3" strokeLinecap="round" />
          <path d="M50,20 L50,50 L70,50" fill="none" stroke="#1e62d0" strokeWidth="3" strokeLinecap="round" strokeDasharray={gamePhase !== 'idle' ? "1,0" : "1,1"} strokeDashoffset={timer % 10}>
            <animateTransform 
              attributeName="transform" 
              attributeType="XML" 
              type="rotate" 
              from="0 50 50" 
              to="360 50 50" 
              dur={gamePhase !== 'idle' ? "60s" : "0s"}
              repeatCount="indefinite" 
            />
          </path>
          <circle cx="50" cy="50" r="4" fill="#1e62d0" />
          <path d="M50,10 L50,15 M90,50 L85,50 M50,90 L50,85 M10,50 L15,50" stroke="#1e62d0" strokeWidth="2" />
          <path d="M25,20 L28,25 M80,25 L75,28 M80,75 L75,72 M25,80 L28,75" stroke="#1e62d0" strokeWidth="1.5" />
          <path d="M40,16 C25,20 15,30 15,50 C15,75 35,85 50,85 C65,85 85,75 85,50 C85,25 65,15 50,15" fill="none" stroke="#1e62d0" strokeWidth="0.5" opacity="0.3" />
        </g>
      </svg>
    </div>
  );

  return (
    <div className="p-4 max-w-6xl mx-auto bg-white rounded-lg shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-blue-600">Talktastic Voice Boardgame</h1>
      </div>

      {/* Scene selector dropdown menu */}
      {showSceneSelector && (
        <div className="scene-dropdown fixed z-50 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200" 
             style={{ 
               top: `${dropdownPosition.top}px`, 
               left: `${dropdownPosition.left}px`,
               width: `${dropdownPosition.width}px`
             }}>
          {/* Filter exam types for current language only */}
          {Object.keys(languageSettings)
            .filter(examType => languageSettings[examType].name === currentLanguageSetting.name)
            .map((type) => (
            <button
              key={type}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-blue-50 ${currentScene === type ? 'bg-blue-100 font-medium' : ''}`}
              onClick={() => changeExamType(type)}
            >
              <span className="flex items-center justify-between">
                {type}
                {currentScene === type && <ChevronDown className="transform rotate-180" size={16} />}
              </span>
            </button>
          ))}
        </div>
      )}

      {showRules && (
        <Alert className="mb-4">
          <AlertTitle>Game Rules</AlertTitle>
          <AlertDescription className="whitespace-pre-line">{gameRules}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-6">
        {/* Left side time settings area */}
        <div className="w-1/4 bg-blue-50 p-4 rounded-lg shadow-md border-2 border-blue-100">
          <h2 className="text-xl font-bold text-center mb-4 text-blue-700">Time Settings</h2>
          
          <div className="mb-8">
            <HandDrawnClock />
          </div>
          
          <div className="mb-6 bg-white p-3 rounded-lg shadow-sm border border-blue-100">
            <h3 className="font-semibold mb-2 text-blue-800">Preparation Time</h3>
            <div className="flex items-center justify-between">
              <button 
                onClick={() => adjustPrepTime(-10)} 
                className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-all transform hover:scale-110 disabled:opacity-50"
                disabled={gamePhase !== 'idle'}
              >
                <HandDrawnButton type="minus" />
              </button>
              <span className="text-2xl font-bold text-blue-600">{prepTime}s</span>
              <button 
                onClick={() => adjustPrepTime(10)} 
                className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-all transform hover:scale-110 disabled:opacity-50"
                disabled={gamePhase !== 'idle'}
              >
                <HandDrawnButton type="plus" />
              </button>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-100">
            <h3 className="font-semibold mb-2 text-blue-800">Practice Time</h3>
            <div className="flex items-center justify-between">
              <button 
                onClick={() => adjustPracticeTime(-30)} 
                className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-all transform hover:scale-110 disabled:opacity-50"
                disabled={gamePhase !== 'idle'}
              >
                <HandDrawnButton type="minus" />
              </button>
              <span className="text-2xl font-bold text-blue-600">{practiceTime}s</span>
              <button 
                onClick={() => adjustPracticeTime(30)} 
                className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-all transform hover:scale-110 disabled:opacity-50"
                disabled={gamePhase !== 'idle'}
              >
                <HandDrawnButton type="plus" />
              </button>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <div className="mb-2 font-semibold text-blue-800">Current Phase</div>
            <div className={`text-xl font-bold p-2 rounded-lg shadow transition-all duration-300
              ${gamePhase === 'idle' ? 'bg-blue-100 text-blue-700' : 
                gamePhase === 'prep' ? 'bg-yellow-100 text-yellow-700' : 
                'bg-green-100 text-green-700'}`}>
              {gamePhase === 'idle' ? 'Waiting to Start' : gamePhase === 'prep' ? 'Preparation' : 'Practice'}
            </div>
          </div>
          
          {/* Yellow note tip */}
          <div className="mt-8 p-4 bg-yellow-100 rounded-lg border-2 border-yellow-200 shadow-md transform rotate-1" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 20px, rgba(255,255,255,0.3) 22px)' }}>
            <div className="text-lg font-bold text-red-600 mb-2">Explore your topic from the following aspects:</div>
            <ul className="text-red-500 pl-6 space-y-1">
              <li>Who?</li>
              <li>When?</li>
              <li>Where?</li>
              <li>Why?</li>
              <li>Have you ever?</li>
            </ul>
          </div>
          
          {/* Current recording language hint */}
          {isRecording && (
            <div className="mt-4 p-2 bg-green-100 rounded-lg text-center border border-green-200">
              <p className="text-sm font-medium text-green-800">
                Current Voice Recognition: {currentLanguageSetting.name}
              </p>
            </div>
          )}
        </div>
        
        {/* Right side board area */}
        <div className="w-3/4">
          <div className="grid grid-cols-4 gap-3 mb-6">
        {currentTopics.map((topic, index) => (
          <div
            key={index}
                className={`p-3 border-2 ${index === position ? 'bg-red-500 text-white' : 'bg-white'} 
                          rounded-lg shadow text-center relative min-h-[80px] flex items-center justify-center
                          transition-all duration-300 hover:shadow-md
                          ${bombPositions.includes(index) ? 'border-yellow-300' : 'border-blue-200'}`}
              >
                <span className="text-md font-medium">{topic}</span>
            {bombPositions.includes(index) && 
                  <span className="absolute top-1 right-1 text-yellow-500 animate-pulse">💣</span>
            }
          </div>
        ))}
      </div>

          <div className="flex justify-between items-center p-4 mb-6 bg-blue-50 rounded-lg shadow border border-blue-100">
            <div className="text-2xl font-bold text-blue-700 flex items-center">
              <span className="mr-2">Score:</span> 0
        </div>
            <div className="text-blue-600 transform transition-all hover:scale-110 duration-300">
          {diceIcons[diceValue - 1]}
        </div>
            <div className="text-2xl font-bold text-red-600 flex items-center">
              <Clock className="mr-2 text-red-600" size={28} />
              <span>{timer}s</span>
        </div>
      </div>

          <div className="text-center mb-6 p-4 bg-white rounded-lg shadow-md border border-blue-100">
            <p className="text-xl mb-4">Current Topic: <strong className="text-green-600 text-2xl">{currentTopics[position]}</strong></p>
            <div className="flex justify-center space-x-6">
          <button
            onClick={rollDice}
            disabled={gamePhase !== 'idle'}
                className={`px-6 py-3 font-bold text-white rounded-full transition-all duration-300 transform hover:scale-105 shadow-md
                          ${gamePhase === 'idle' ? 'bg-blue-500 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed opacity-70'}`}
          >
                <div className="flex items-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" className="mr-2" style={{ filter: 'url(#shaky-button)' }}>
                    <path d="M5,5 L19,5 L19,19 L5,19 Z" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="12" cy="12" r="2" fill="white" />
                  </svg>
            Roll Dice
                </div>
          </button>
          <button
                onClick={toggleRecording}
                disabled={gamePhase !== 'practice'}
                className={`px-6 py-3 font-bold text-white rounded-full transition-all duration-300 transform hover:scale-105 shadow-md flex items-center justify-center
                          ${gamePhase !== 'practice' ? 
                            'opacity-70 cursor-not-allowed bg-gray-400' : 
                            isRecording ? 
                              'bg-red-500 hover:bg-red-600 animate-pulse' : 
                              'bg-green-500 hover:bg-green-600'}`}
          >
            <Mic className="mr-2" size={20} />
                {isRecording ? 'Recording Voice' : 'Start Voice Input'}
          </button>
          <button
            onClick={updateVocab}
                className="px-6 py-3 font-bold text-white bg-purple-500 rounded-full hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 shadow-md flex items-center justify-center"
          >
            <RefreshCw className="mr-2" size={20} />
            Update Vocabulary
          </button>
        </div>
            
            {/* Voice to text result display */}
            {(transcription || isRecording) && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-left">
                <h3 className="font-semibold text-gray-700 mb-1">Speech Recognition:</h3>
                <p className="text-gray-600 italic">{transcription || 'Listening...'}</p>
              </div>
            )}
            
            {/* View analysis report option */}
            {showTranscriptionOptions && (
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200 shadow-md">
                <div className="flex justify-center">
                  <button 
                    onClick={viewPaidAnalysis}
                    className="px-6 py-3 bg-purple-500 text-white font-bold rounded-lg hover:bg-purple-600 transition-all"
                  >
                    View Premium Analysis
                  </button>
                </div>
              </div>
            )}
      </div>

      {bombTask && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300 shadow-md animate-pulse">
              <div className="flex items-center">
                <span className="text-yellow-500 text-2xl mr-2">💣</span>
                <p className="font-bold text-lg text-yellow-700">Bomb Task</p>
              </div>
              <p className="mt-2 text-yellow-800 font-medium">{bombTask}</p>
        </div>
      )}

        </div>
      </div>
    </div>
  );
};

export default TalktasticVoiceGame;
